import * as React from 'react'
  /* @jsx mdx */
import { mdx } from '@mdx-js/react';
/* @jsxRuntime classic */
/* @jsx mdx */

export const _frontmatter = {};
const layoutProps = {
  _frontmatter
};
const MDXLayout = "wrapper";
export default function MDXContent({
  components,
  ...props
}) {
  return <MDXLayout {...layoutProps} {...props} components={components} mdxType="MDXLayout">
    <h1>{`Right to left languages`}</h1>
    <p>{`Package `}<inlineCode parentName="p">{`orbit-components`}</inlineCode>{` supports right to left languages. It’s done with `}<inlineCode parentName="p">{`theme`}</inlineCode>{` context from `}<inlineCode parentName="p">{`styled-components`}</inlineCode>{`.`}</p>
    <p>{`If you need to enable `}<inlineCode parentName="p">{`rtl`}</inlineCode>{` inside `}<inlineCode parentName="p">{`orbit-components`}</inlineCode>{`, set it inside `}<inlineCode parentName="p">{`OrbitProvider`}</inlineCode>{`:`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-jsx"
      }}>{`import { OrbitProvider, defaultTheme } from "@kiwicom/orbit-components";

<OrbitProvider useId={React.useId} theme={{ ...defaultTheme, rtl: true }}>
  <App />
</OrbitProvider>;
`}</code></pre>
    <p>{`It’s also important to pass some `}<inlineCode parentName="p">{`orbit`}</inlineCode>{` object, in our case `}<inlineCode parentName="p">{`defaultTheme`}</inlineCode>{`.
With this approach, all `}<inlineCode parentName="p">{`orbit-components`}</inlineCode>{` will be ready to use with some right to left language.`}</p>
    <blockquote>
      <p parentName="blockquote">{`In addition, you need to set up `}<inlineCode parentName="p">{`direction`}</inlineCode>{` on `}<inlineCode parentName="p">{`html`}</inlineCode>{` to have value `}<inlineCode parentName="p">{`rtl`}</inlineCode>{`.`}</p>
    </blockquote>
    <h2>{`Utilities`}</h2>
    <p><inlineCode parentName="p">{`styled-components`}</inlineCode>{` doesn’t know automatically how to switch `}<inlineCode parentName="p">{`margin`}</inlineCode>{`, `}<inlineCode parentName="p">{`padding`}</inlineCode>{` and i.e. `}<inlineCode parentName="p">{`left`}</inlineCode>{` properties to the other direction. So it’s necessary to use some `}<inlineCode parentName="p">{`utilities`}</inlineCode>{` inside your styles.`}</p>
    <h3>{`rtlSpacing`}</h3>
    <p>{`Function `}<inlineCode parentName="p">{`rtlSpacing`}</inlineCode>{` switches the values for properties `}<inlineCode parentName="p">{`margin`}</inlineCode>{` and `}<inlineCode parentName="p">{`padding`}</inlineCode>{`.`}</p>
    <h4>{`Usage`}</h4>
    <pre><code parentName="pre" {...{
        "className": "language-jsx"
      }}>{`import { rtlSpacing } from "@kiwicom/orbit-components/lib/utils/rtl";

const Component = styled.div\`
  margin: \${rtlSpacing("10px 20px 30px 40px")};
\`;
`}</code></pre>
    <p>{`If the `}<inlineCode parentName="p">{`rtl`}</inlineCode>{` inside theme context will be `}<inlineCode parentName="p">{`true`}</inlineCode>{`, the margin for our component will be `}<inlineCode parentName="p">{`10px 40px 30px 20px`}</inlineCode>{`. Otherwise the default passed value.`}</p>
    <p>{`This function accepts only one parameter - `}<inlineCode parentName="p">{`string`}</inlineCode>{`.`}</p>
    <hr></hr>
    <h3>{`left`}</h3>
    <p>{`The `}<inlineCode parentName="p">{`left`}</inlineCode>{` utility returns `}<inlineCode parentName="p">{`right`}</inlineCode>{`, if the `}<inlineCode parentName="p">{`rtl`}</inlineCode>{` is set to `}<inlineCode parentName="p">{`true`}</inlineCode>{`. Otherwise, it returns `}<inlineCode parentName="p">{`left`}</inlineCode>{`.`}</p>
    <h4>{`Usage`}</h4>
    <pre><code parentName="pre" {...{
        "className": "language-jsx"
      }}>{`import { left } from "@kiwicom/orbit-components/lib/utils/rtl";

const Component = styled.div\`
  \${left}: 0;
\`;
`}</code></pre>
    <p>{`This utility doesn’t accept any parameter.`}</p>
    <hr></hr>
    <h3>{`right`}</h3>
    <p>{`The `}<inlineCode parentName="p">{`right`}</inlineCode>{` utility returns `}<inlineCode parentName="p">{`left`}</inlineCode>{`, if the `}<inlineCode parentName="p">{`rtl`}</inlineCode>{` is set to `}<inlineCode parentName="p">{`true`}</inlineCode>{`. Otherwise, it returns `}<inlineCode parentName="p">{`right`}</inlineCode>{`.`}</p>
    <h4>{`Usage`}</h4>
    <pre><code parentName="pre" {...{
        "className": "language-jsx"
      }}>{`import { right } from "@kiwicom/orbit-components/lib/utils/rtl";

const Component = styled.div\`
  \${right}: 0;
\`;
`}</code></pre>
    <p>{`This utility doesn’t accept any parameter.`}</p>
    <hr></hr>
    <h3>{`borderRadius`}</h3>
    <p>{`Function `}<inlineCode parentName="p">{`borderRadius`}</inlineCode>{` switches the values for `}<inlineCode parentName="p">{`border-radius`}</inlineCode>{` property.`}</p>
    <h4>{`Usage`}</h4>
    <pre><code parentName="pre" {...{
        "className": "language-jsx"
      }}>{`import { borderRadius } from "@kiwicom/orbit-components/lib/utils/rtl";

const Component = styled.div\`
  border-radius: \${borderRadius("0 3px 3px 0")};
\`;
`}</code></pre>
    <p>{`If the `}<inlineCode parentName="p">{`rtl`}</inlineCode>{` inside theme context will be `}<inlineCode parentName="p">{`true`}</inlineCode>{`, the `}<inlineCode parentName="p">{`border-radius`}</inlineCode>{` for our component will be `}<inlineCode parentName="p">{`3px 0 0 3px`}</inlineCode>{`. Otherwise the default passed value.`}</p>
    <p>{`This function accepts only one parameter - `}<inlineCode parentName="p">{`string`}</inlineCode>{`.`}</p>
    <hr></hr>
    <h3>{`textAlign`}</h3>
    <p>{`Function `}<inlineCode parentName="p">{`textAlign`}</inlineCode>{` switches the values for `}<inlineCode parentName="p">{`text-align`}</inlineCode>{` property.`}</p>
    <h4>{`Usage with left`}</h4>
    <pre><code parentName="pre" {...{
        "className": "language-jsx"
      }}>{`import { textAlign } from "@kiwicom/orbit-components/lib/utils/rtl";

const Component = styled.div\`
  text-align: \${textAlign("left")};
\`;
`}</code></pre>
    <p>{`If the `}<inlineCode parentName="p">{`rtl`}</inlineCode>{` inside theme context will be `}<inlineCode parentName="p">{`true`}</inlineCode>{`, the `}<inlineCode parentName="p">{`text-align`}</inlineCode>{` for our component will be `}<inlineCode parentName="p">{`right`}</inlineCode>{`. Otherwise the default passed value.`}</p>
    <h4>{`Usage with right`}</h4>
    <pre><code parentName="pre" {...{
        "className": "language-jsx"
      }}>{`import { textAlign } from "@kiwicom/orbit-components/lib/utils/rtl";

const Component = styled.div\`
  text-align: \${textAlign("right")};
\`;
`}</code></pre>
    <p>{`If the `}<inlineCode parentName="p">{`rtl`}</inlineCode>{` inside theme context will be `}<inlineCode parentName="p">{`true`}</inlineCode>{`, the `}<inlineCode parentName="p">{`text-align`}</inlineCode>{` for our component will be `}<inlineCode parentName="p">{`left`}</inlineCode>{`. Otherwise the default passed value.`}</p>
    <p>{`This function accepts only one parameter - `}<inlineCode parentName="p">{`string`}</inlineCode>{` which needs to be either `}<inlineCode parentName="p">{`left`}</inlineCode>{` or `}<inlineCode parentName="p">{`right`}</inlineCode>{`.`}</p>
    <h3>{`translate3d`}</h3>
    <p>{`Function `}<inlineCode parentName="p">{`translate3d`}</inlineCode>{` switches the values for `}<inlineCode parentName="p">{`transform`}</inlineCode>{` property.`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-jsx"
      }}>{`import { translate3d } from "@kiwicom/orbit-components/lib/utils/rtl";

const Component = styled.div\`
  transform: \${translate3d("400px, 0, 0")};
\`;
`}</code></pre>
    <p>{`If the `}<inlineCode parentName="p">{`rtl`}</inlineCode>{` inside theme context will be `}<inlineCode parentName="p">{`true`}</inlineCode>{`, the `}<inlineCode parentName="p">{`translate3d`}</inlineCode>{` first value for our component will be `}<inlineCode parentName="p">{`-400px`}</inlineCode>{`. Otherwise the default passed value.`}</p>
    <p>{`This function accepts only one parameter - `}<inlineCode parentName="p">{`string`}</inlineCode>{`.`}</p>

    </MDXLayout>;
}
;
MDXContent.isMDXComponent = true;
      